import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Star, Sparkles, Camera, Zap, Users, Mail, Phone, MapPin, Clock, Check, Award, Medal, Trophy, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import QuoteBuilder from '../components/QuoteBuilder';
import BookingCalendar from '../components/BookingCalendar';
import ContactForm from '../components/ContactForm';
import { packages, features, testimonials, faqs } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Home = () => {
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [showContact, setShowContact] = useState(false);

  // Notify admin of visitor
  useEffect(() => {
    const notifyVisit = async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/notify-visit`);
      } catch (e) {
        // Silent fail - don't affect user experience
      }
    };
    
    // Only notify once per session
    if (!sessionStorage.getItem('visited')) {
      sessionStorage.setItem('visited', 'true');
      notifyVisit();
    }
  }, []);

  // Initialize Tawk.to live chat
  useEffect(() => {
    // Tawk.to script
    var Tawk_API = Tawk_API || {}, Tawk_LoadStart = new Date();
    (function(){
      var s1 = document.createElement("script"), s0 = document.getElementsByTagName("script")[0];
      s1.async = true;
      s1.src = 'https://embed.tawk.to/5f7c8c4cf0e7167d00141f4e/default';
      s1.charset = 'UTF-8';
      s1.setAttribute('crossorigin', '*');
      s0.parentNode.insertBefore(s1, s0);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-8 h-8 text-emerald-700" />
            <span className="text-2xl font-bold text-black">MySelfie<span className="text-emerald-700">Moments</span></span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#packages" className="text-gray-700 hover:text-emerald-700 transition-colors">Packages</a>
            <a href="#features" className="text-gray-700 hover:text-emerald-700 transition-colors">Features</a>
            <a href="#booking" className="text-gray-700 hover:text-emerald-700 transition-colors">Booking</a>
            <a href="#contact" className="text-gray-700 hover:text-emerald-700 transition-colors">Contact</a>
          </nav>
          <Button onClick={() => setShowQuoteBuilder(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white">
            Get Quote
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto text-center">
          <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100" role="text">London & Hertfordshire's Premier Photo Booth</Badge>
          <h1 className="text-5xl md:text-7xl font-bold text-black mb-6 leading-tight">
            Professional Photo Booth Hire in <span className="text-emerald-700">London & Hertfordshire</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Professional photo booth hire for weddings, parties, and corporate events. 
            Unlimited fun, instant sharing, unforgettable memories.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setShowQuoteBuilder(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white text-lg px-8 py-6">
              <Sparkles className="mr-2 h-5 w-5" />
              Build Your Quote
            </Button>
            <Button size="lg" variant="outline" onClick={() => setShowBooking(true)} className="border-2 border-black text-black hover:bg-black hover:text-white text-lg px-8 py-6">
              <Calendar className="mr-2 h-5 w-5" />
              Check Availability
            </Button>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-700" />
              <span>Instant Digital Delivery</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-700" />
              <span>Professional Attendants</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-700" />
              <span>Unlimited Captures</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">Capture The Fun, Share The Moments</h2>
              <p className="text-lg text-gray-600 mb-6">
                Want your guests to have fun, laugh together and leave with unforgettable memories? That's exactly what we do.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                At My Selfie Moments, we make it easy to capture the best moments from your event with our stylish, digital open-air photo booths.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're planning a wedding, birthday party, corporate event or private celebration, our friendly, experienced team will guide you through the booking process — helping you choose the perfect package for your event.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-emerald-700 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-black">Instant prints & digital sharing</p>
                    <p className="text-gray-600 text-sm">Your guests get their photos instantly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-emerald-700 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-black">Unlimited photos, GIFs & boomerangs</p>
                    <p className="text-gray-600 text-sm">No limits on the fun!</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-emerald-700 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-black">Fun props & custom print designs</p>
                    <p className="text-gray-600 text-sm">Personalized for your event</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-emerald-700 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-black">Professional set-up & support included</p>
                    <p className="text-gray-600 text-sm">We handle everything on the day</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-gray-200 rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_photobooth-express/artifacts/1052mqgi_IMG_6223.jpeg" 
                  alt="Professional open-air photo booth by MySelfie Moments at London wedding - guests enjoying unlimited captures with instant digital sharing" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-emerald-700 text-white p-6 rounded-lg shadow-xl max-w-xs">
                <p className="text-3xl font-bold mb-1">Open-Air</p>
                <p className="text-sm">Modern, stylish photo booths for London & Hertfordshire events</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple steps to amazing memories</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <Card className="text-center border-2 hover:border-emerald-700 transition-all">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-emerald-700">1</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Check Availability</h3>
                <p className="text-sm text-gray-600">See if we're available for your date</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-emerald-700 transition-all">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-emerald-700">2</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Pick Your Package</h3>
                <p className="text-sm text-gray-600">Choose the perfect package for you</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-emerald-700 transition-all">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-emerald-700">3</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Personalise</h3>
                <p className="text-sm text-gray-600">Custom prints & props for your event</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-emerald-700 transition-all">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-emerald-700">4</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">We Set Up</h3>
                <p className="text-sm text-gray-600">Professional delivery, setup & hosting</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 hover:border-emerald-700 transition-all">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-emerald-700">5</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">You Enjoy!</h3>
                <p className="text-sm text-gray-600">Capture, print, share & create memories</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">All packages include our complete feature set</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-emerald-700 transition-all hover:shadow-lg">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <Sparkles className="h-6 w-6 text-emerald-700" />
                  </div>
                  <p className="font-semibold text-black">{feature}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Choose Your Package</h2>
            <p className="text-xl text-gray-600">Same amazing features, just pick your duration</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
                <Card key={pkg.id} className={`relative border-2 hover:shadow-xl transition-all ${pkg.popular ? 'border-emerald-700 shadow-lg' : pkg.id === 'bronze' ? 'border-amber-600' : pkg.id === 'silver' ? 'border-gray-400' : pkg.id === 'gold' ? 'border-yellow-500' : 'border-slate-600'}`}>
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-emerald-700 text-white">Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-2xl text-black">{pkg.name}</CardTitle>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${pkg.id === 'bronze' ? 'bg-amber-50' : pkg.id === 'silver' ? 'bg-gray-50' : pkg.id === 'gold' ? 'bg-yellow-50' : 'bg-slate-50'}`}>
                        {pkg.id === 'bronze' && <Award className="h-5 w-5 text-amber-600" />}
                        {pkg.id === 'silver' && <Medal className="h-5 w-5 text-gray-400" />}
                        {pkg.id === 'gold' && <Trophy className="h-5 w-5 text-yellow-500" />}
                        {pkg.id === 'corporate' && <Briefcase className="h-5 w-5 text-slate-600" />}
                      </div>
                    </div>
                    <CardDescription>{pkg.description}</CardDescription>
                  </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-black">{pkg.isPOA ? 'POA' : `£${pkg.basePrice}`}</div>
                    {!pkg.isPOA && <div className="text-sm text-gray-600">Fixed price</div>}
                    {pkg.isPOA && <div className="text-sm text-gray-600">Contact us for pricing</div>}
                  </div>
                  <div className="space-y-3 mb-6">
                    {!pkg.isPOA && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-emerald-700" />
                        <span>{pkg.duration} hours coverage</span>
                      </div>
                    )}
                    {pkg.isPOA && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-emerald-700" />
                        <span>Flexible duration</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-emerald-700" />
                      <span>Professional attendant</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Zap className="h-4 w-4 text-emerald-700" />
                      <span>All features included</span>
                    </div>
                  </div>
                  <Button onClick={() => setShowQuoteBuilder(true)} className={`w-full ${pkg.popular ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'bg-black hover:bg-gray-800 text-white'}`}>
                    {pkg.isPOA ? 'Contact Us' : 'Get Custom Quote'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-600">Discounts available for weekday bookings and charity events</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black text-white px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Happy Clients</h2>
            <p className="text-xl text-gray-300">See what our customers say about us</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white/5 border-white/10 backdrop-blur-sm">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-emerald-600 text-emerald-600" />
                    ))}
                  </div>
                  <p className="text-white mb-4">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.event}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Check Availability</h2>
            <p className="text-xl text-gray-600">See when we're available for your special event</p>
          </div>
          <BookingCalendar />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">Let's make your event unforgettable</p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Camera className="w-6 h-6 text-emerald-600" />
                <span className="text-xl font-bold">MySelfie<span className="text-emerald-600">Moments</span></span>
              </div>
              <p className="text-gray-400">Professional photo booth hire serving London, Hertfordshire, and surrounding areas. Specializing in wedding photo booths, corporate event photography, and party entertainment.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-lg">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#packages" className="hover:text-emerald-600 transition-colors">Packages</a></li>
                <li><a href="#features" className="hover:text-emerald-600 transition-colors">Features</a></li>
                <li><a href="#booking" className="hover:text-emerald-600 transition-colors">Booking</a></li>
                <li><a href="#contact" className="hover:text-emerald-600 transition-colors">Contact</a></li>
                <li><Link to="/terms" className="hover:text-emerald-600 transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy" className="hover:text-emerald-600 transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-lg">Contact Us</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-emerald-600" />
                  <a href="mailto:info@myselfiemoments.co.uk" className="hover:text-emerald-600 transition-colors">info@myselfiemoments.co.uk</a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-600" />
                  <a href="tel:+44" className="hover:text-emerald-600 transition-colors">Contact via form</a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-emerald-600" />
                  <span>London & Hertfordshire</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 MySelfie Moments. All rights reserved.</p>
            <div className="mt-3 flex justify-center gap-4 text-sm">
              <Link to="/privacy" className="hover:text-emerald-600 transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-emerald-600 transition-colors">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Quote Builder Modal */}
      {showQuoteBuilder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Build Your Quote</h2>
              <Button variant="ghost" onClick={() => setShowQuoteBuilder(false)}>✕</Button>
            </div>
            <div className="p-6">
              <QuoteBuilder onClose={() => setShowQuoteBuilder(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Check Availability</h2>
              <Button variant="ghost" onClick={() => setShowBooking(false)}>✕</Button>
            </div>
            <div className="p-6">
              <BookingCalendar />
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContact && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Contact Us</h2>
              <Button variant="ghost" onClick={() => setShowContact(false)}>✕</Button>
            </div>
            <div className="p-6">
              <ContactForm />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;